import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  LuVideo,
  LuMusic,
  LuImage,
  LuX,
  LuPlay,
  LuPause,
  LuFiles,
  LuCircleDot,
  LuEye,
  LuArrowRight,
} from "react-icons/lu";
import i18n from "i18next";

import AppModal from "../../../../common/modal/AppModal";
import { getYoutubeThumbnailUrl, isYoutubeUrl } from "../../../../../../common/youtube_tools";
import {
  getPluginImageUrl,
  extractContentImageUrls,
  extractBackgroundImageUrl,
} from "../../../../../utils/collectSceneImages.es6";
import "./KnollingView.scss";

const KnollingView = ({
  boxes,
  soundList,
  pluginToolbars,
  navItems,
  navItemSelected,
  onClose,
  onNavigateToScene,
  embedded = false,
}) => {
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeMenu, setActiveMenu] = useState(null);
  const [showCurrentSceneOnly, setShowCurrentSceneOnly] = useState(true);
  const audioRef = useRef(new Audio());

  useEffect(
    () => () => {
      try {
        audioRef.current && audioRef.current.pause();
      } catch (e) {}
    },
    []
  );

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any menu
      const isMenuClick =
        event.target.closest(".knolling-view__item-menu") ||
        event.target.closest(".knolling-view__item-menu-btn");

      if (!isMenuClick && activeMenu) {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [activeMenu]);

  const mediaAssets = useMemo(() => {
    const assets = {
      videos: [],
      images: [],
      audios: [],
    };

    const seenUrls = new Set();

    // Get current scene's boxes if filtering
    let currentSceneBoxIds = new Set();
    if (showCurrentSceneOnly && navItemSelected && navItems && navItems[navItemSelected]) {
      const currentNavItem = navItems[navItemSelected];
      if (currentNavItem.boxes) {
        currentSceneBoxIds = new Set(currentNavItem.boxes);
      }
    }

    console.log("KnollingView - boxes:", boxes);
    console.log("KnollingView - pluginToolbars:", pluginToolbars);
    console.log("KnollingView - soundList:", soundList);
    console.log("KnollingView - showCurrentSceneOnly:", showCurrentSceneOnly);
    console.log("KnollingView - currentSceneBoxIds:", currentSceneBoxIds);

    // Collect from all boxes
    Object.values(boxes || {}).forEach((box) => {
      if (!box) return;

      // Skip if filtering by current scene and box is not in current scene
      if (showCurrentSceneOnly && currentSceneBoxIds.size > 0 && !currentSceneBoxIds.has(box.id)) {
        return;
      }
      if (!box) return;

      const toolbar = pluginToolbars?.[box.id];
      const pluginId = toolbar?.pluginId;

      // Videos - EnrichedPlayer plugin
      if (pluginId === "EnrichedPlayer") {
        const url = toolbar.state?.url;
        if (url && !seenUrls.has(url)) {
          seenUrls.add(url);
          assets.videos.push({
            id: box.id,
            name: toolbar.state?.name || toolbar.state?.title || "Video",
            thumbUrl: toolbar.state?.thumbnail || url,
            url: url,
          });
        }
      }

      // Images - BasicImage, HotspotImages, HotspotsImage plugins (shared
      // with AnimatorPanel's "All images" source via collectSceneImages.es6)
      const pluginImageUrl = getPluginImageUrl(toolbar);
      if (pluginImageUrl && !seenUrls.has(pluginImageUrl)) {
        seenUrls.add(pluginImageUrl);
        assets.images.push({
          id: box.id,
          name: toolbar.state?.alt || toolbar.state?.name || "Image",
          thumbUrl: pluginImageUrl,
          url: pluginImageUrl,
        });
      }

      // Check for images in box content (HTML)
      extractContentImageUrls(box).forEach((url) => {
        if (!seenUrls.has(url)) {
          seenUrls.add(url);
          assets.images.push({
            id: box.id + "_img_" + url.substring(0, 20),
            name: "Content Image",
            thumbUrl: url,
            url: url,
          });
        }
      });

      // Background images from box styles
      const bgImageUrl = extractBackgroundImageUrl(box);
      if (bgImageUrl && !seenUrls.has(bgImageUrl)) {
        seenUrls.add(bgImageUrl);
        assets.images.push({
          id: box.id + "_bg",
          name: "Background Image",
          thumbUrl: bgImageUrl,
          url: bgImageUrl,
        });
      }
    });

    // Collect from sound list
    Object.entries(soundList || {}).forEach(([navId, sceneSounds]) => {
      // Skip if filtering by current scene and this is not the current scene
      if (showCurrentSceneOnly && navItemSelected && navId !== String(navItemSelected)) {
        return;
      }

      if (Array.isArray(sceneSounds)) {
        sceneSounds.forEach((sound) => {
          if (sound.file && !seenUrls.has(sound.file)) {
            seenUrls.add(sound.file);
            assets.audios.push({
              id: sound.id || sound.file,
              name: sound.name || "Audio",
              url: sound.file,
              duration: sound.endTime - sound.startTime,
            });
          }
        });
      }
    });

    console.log("KnollingView - collected assets:", assets);
    return assets;
  }, [boxes, soundList, pluginToolbars, showCurrentSceneOnly, navItemSelected, navItems]);

  const totalAssets =
    mediaAssets.videos.length + mediaAssets.images.length + mediaAssets.audios.length;

  const handlePlayAudio = (audio) => {
    if (playingAudioId === audio.id) {
      // Pause if already playing
      audioRef.current.pause();
      setPlayingAudioId(null);
    } else {
      // Play new audio
      audioRef.current.src = audio.url;
      audioRef.current.play();
      setPlayingAudioId(audio.id);

      // Reset playing state when audio ends
      audioRef.current.onended = () => {
        setPlayingAudioId(null);
      };
    }
  };

  const handlePreview = (media, type) => {
    setPreviewMedia({ ...media, type });
  };

  const handleClosePreview = () => {
    setPreviewMedia(null);
  };

  const handleMenuToggle = (mediaId, event) => {
    event.stopPropagation();
    setActiveMenu(activeMenu === mediaId ? null : mediaId);
  };

  const handleGoToScene = (mediaId, event) => {
    event.stopPropagation();

    // Find the box
    const box = boxes[mediaId];
    if (!box) {
      console.warn("Box not found:", mediaId);
      setActiveMenu(null);
      return;
    }

    // Find the scene (navItem) that contains this box
    let targetNavId = null;

    // Check if box has a parent that's a navItem
    if (box.parent) {
      // First check if parent is directly a navItem
      if (navItems && navItems[box.parent]) {
        targetNavId = box.parent;
      } else {
        // If not, search through navItems to find which one contains this box
        for (const navId in navItems) {
          const navItem = navItems[navId];
          if (navItem.boxes && navItem.boxes.includes(mediaId)) {
            targetNavId = navId;
            break;
          }
          // Also check if the parent box is in this navItem
          if (navItem.boxes && navItem.boxes.includes(box.parent)) {
            targetNavId = navId;
            break;
          }
        }
      }
    }

    if (targetNavId && onNavigateToScene) {
      onNavigateToScene(targetNavId);
    } else {
      console.warn("Could not find scene for box:", mediaId);
    }

    setActiveMenu(null);
  };

  const handlePreviewFromMenu = (media, type, event) => {
    event.stopPropagation();
    handlePreview(media, type);
    setActiveMenu(null);
  };

  const categories = [
    { id: "all", label: i18n.t("All") || "Tümü", icon: LuFiles, count: totalAssets },
    {
      id: "videos",
      label: i18n.t("Videos") || "Videolar",
      icon: LuVideo,
      count: mediaAssets.videos.length,
    },
    {
      id: "images",
      label: i18n.t("Images") || "Görseller",
      icon: LuImage,
      count: mediaAssets.images.length,
    },
    {
      id: "audios",
      label: i18n.t("Audios") || "Sesler",
      icon: LuMusic,
      count: mediaAssets.audios.length,
    },
  ];

  const filteredAssets = useMemo(() => {
    if (activeCategory === "all") return mediaAssets;
    return {
      videos: activeCategory === "videos" ? mediaAssets.videos : [],
      images: activeCategory === "images" ? mediaAssets.images : [],
      audios: activeCategory === "audios" ? mediaAssets.audios : [],
    };
  }, [activeCategory, mediaAssets]);

  const knollingBody = (
    <>
      <div className="knolling-view">
        <div className="knolling-view__sidebar">
          <div className="knolling-view__sidebar-header">
            <h4>{i18n.t("knollingView.categories") || "Kategoriler"}</h4>
          </div>
          <div className="knolling-view__filter-section">
            <label className="knolling-view__filter-checkbox">
              <input
                type="checkbox"
                checked={showCurrentSceneOnly}
                onChange={(e) => setShowCurrentSceneOnly(e.target.checked)}
              />
              <span>{i18n.t("knollingView.currentSceneOnly") || "Sadece bu sahne"}</span>
            </label>
          </div>
          <div className="knolling-view__categories">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  className={`knolling-view__category${activeCategory === category.id ? " knolling-view__category--active" : ""}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  <Icon size={18} />
                  <span className="knolling-view__category-label">
                    {i18n.t("knollingView.all") || category.label}
                  </span>
                  <span className="knolling-view__category-count">{category.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="knolling-view__content">
          {totalAssets === 0 ? (
            <div className="knolling-view__empty">
              <LuFiles size={64} />
              <p>{i18n.t("knollingView.noMedia") || "Henüz medya varlığı bulunmuyor."}</p>
            </div>
          ) : (
            <>
              {filteredAssets.videos.length > 0 && (
                <div className="knolling-view__section">
                  <div className="knolling-view__section-header">
                    <LuVideo size={16} />
                    <h4>
                      {i18n.t("knollingView.videos") || "Videolar"} ({filteredAssets.videos.length})
                    </h4>
                  </div>
                  <div className="knolling-view__grid">
                    {filteredAssets.videos.map((video) => (
                      <div
                        key={video.id}
                        className="knolling-view__item"
                        onClick={() => handlePreview(video, "video")}
                      >
                        <div className="knolling-view__item-thumb">
                          {getYoutubeThumbnailUrl(video.url) ? (
                            <img
                              src={getYoutubeThumbnailUrl(video.url)}
                              alt={video.name}
                              className="knolling-view__video-preview"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextElementSibling.style.display = "flex";
                              }}
                            />
                          ) : (
                            <video
                              src={video.url}
                              className="knolling-view__video-preview"
                              muted
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextElementSibling.style.display = "flex";
                              }}
                            />
                          )}
                          <div className="knolling-view__item-thumb-fallback">
                            <LuVideo size={48} />
                          </div>
                          {!showCurrentSceneOnly && (
                            <>
                              <button
                                className="knolling-view__item-menu-btn"
                                onClick={(e) => handleMenuToggle(video.id, e)}
                              >
                                <LuCircleDot size={18} />
                              </button>
                              {activeMenu === video.id && (
                                <div className="knolling-view__item-menu">
                                  <button
                                    className="knolling-view__item-menu-item"
                                    onClick={(e) => handlePreviewFromMenu(video, "video", e)}
                                  >
                                    <LuEye size={16} />
                                    <span>{i18n.t("knollingView.preview") || "Önizle"}</span>
                                  </button>
                                  <button
                                    className="knolling-view__item-menu-item"
                                    onClick={(e) => handleGoToScene(video.id, e)}
                                  >
                                    <LuArrowRight size={16} />
                                    <span>{i18n.t("knollingView.goToScene") || "Sahneye Git"}</span>
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="knolling-view__item-info">
                          <p className="knolling-view__item-name" title={video.name}>
                            {video.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {filteredAssets.images.length > 0 && (
                <div className="knolling-view__section">
                  <div className="knolling-view__section-header">
                    <LuImage size={16} />
                    <h4>
                      {i18n.t("knollingView.images") || "Görseller"} ({filteredAssets.images.length}
                      )
                    </h4>
                  </div>
                  <div className="knolling-view__grid">
                    {filteredAssets.images.map((image) => (
                      <div
                        key={image.id}
                        className="knolling-view__item"
                        onClick={() => handlePreview(image, "image")}
                      >
                        <div className="knolling-view__item-thumb">
                          <img
                            src={image.thumbUrl}
                            alt={image.name}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextElementSibling.style.display = "flex";
                            }}
                          />
                          <div className="knolling-view__item-thumb-fallback">
                            <LuImage size={48} />
                          </div>
                          {!showCurrentSceneOnly && (
                            <>
                              <button
                                className="knolling-view__item-menu-btn"
                                onClick={(e) => handleMenuToggle(image.id, e)}
                              >
                                <LuCircleDot size={18} />
                              </button>
                              {activeMenu === image.id && (
                                <div className="knolling-view__item-menu">
                                  <button
                                    className="knolling-view__item-menu-item"
                                    onClick={(e) => handlePreviewFromMenu(image, "image", e)}
                                  >
                                    <LuEye size={16} />
                                    <span>{i18n.t("knollingView.preview") || "Önizle"}</span>
                                  </button>
                                  <button
                                    className="knolling-view__item-menu-item"
                                    onClick={(e) => handleGoToScene(image.id, e)}
                                  >
                                    <LuArrowRight size={16} />
                                    <span>{i18n.t("knollingView.goToScene") || "Sahneye Git"}</span>
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="knolling-view__item-info">
                          <p className="knolling-view__item-name" title={image.name}>
                            {image.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {filteredAssets.audios.length > 0 && (
                <div className="knolling-view__section">
                  <div className="knolling-view__section-header">
                    <LuMusic size={16} />
                    <h4>
                      {i18n.t("knollingView.audios") || "Sesler"} ({filteredAssets.audios.length})
                    </h4>
                  </div>
                  <div className="knolling-view__grid knolling-view__grid--audio">
                    {filteredAssets.audios.map((audio) => {
                      const isPlaying = playingAudioId === audio.id;
                      return (
                        <div
                          key={audio.id}
                          className="knolling-view__item knolling-view__item--audio"
                        >
                          <div className="knolling-view__item-audio-icon">
                            <LuMusic size={32} />
                          </div>
                          <div className="knolling-view__item-info">
                            <p className="knolling-view__item-name" title={audio.name}>
                              {audio.name}
                            </p>
                          </div>
                          <button
                            className={`knolling-view__play-btn ${isPlaying ? "knolling-view__play-btn--playing" : ""}`}
                            onClick={() => handlePlayAudio(audio)}
                            title={
                              isPlaying
                                ? i18n.t("knollingView.pause") || "Duraklat"
                                : i18n.t("knollingView.play") || "Oynat"
                            }
                          >
                            {isPlaying ? <LuPause size={20} /> : <LuPlay size={20} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {previewMedia && (
        <div className="knolling-view__preview-modal" onClick={handleClosePreview}>
          <div className="knolling-view__preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="knolling-view__preview-close" onClick={handleClosePreview}>
              <LuX size={24} />
            </button>
            <div className="knolling-view__preview-media">
              {previewMedia.type === "video" && isYoutubeUrl(previewMedia.url) ? (
                // The stored URL is already an embed URL — play it in an iframe.
                <iframe
                  src={previewMedia.url}
                  title={previewMedia.name}
                  className="knolling-view__preview-video"
                  style={{ width: "100%", aspectRatio: "16 / 9", border: 0 }}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              ) : previewMedia.type === "video" ? (
                <video
                  src={previewMedia.url}
                  controls
                  autoPlay
                  className="knolling-view__preview-video"
                />
              ) : (
                <img
                  src={previewMedia.thumbUrl}
                  alt={previewMedia.name}
                  className="knolling-view__preview-image"
                />
              )}
            </div>
            <div className="knolling-view__preview-info">
              <h4>{previewMedia.name}</h4>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return knollingBody;

  return (
    <AppModal
      show={true}
      onClose={onClose}
      title={i18n.t("knollingView.title") || "Medya Varlıkları"}
      size="xl"
      className="knolling-view-modal"
      bodyClassName="knolling-view__modal-body"
    >
      {knollingBody}
    </AppModal>
  );
};

export default KnollingView;
